import React, { useEffect, useContext } from 'react';
import {
  Button, Typography
} from '@one-thd/sui-atomic-components';
import PropTypes from 'prop-types';
import { initialOrderPreferences } from './tableData';
import ErrorBoundary from '../contexts/ErrorBoundary';
import { OrderBannerContext, BannerProvider } from '../contexts/OrderBannerContext';
import { updateOrderPreferences, retrieveOrderPreferences } from '../apis/orders';
import {
  determineEReceiptSuccessText,
  determineSuccessText, setupEReceiptPreferenceUpdate,
  setupHandleOrderCommunicationsRetrieve
} from '../apis/common';
import OrderPreferencesCard from './common/OrderPreferencesCard';
import ProEReceiptPreferences from './ProEReceiptPreferences';
import OrderStatusConfirmationModal from './SmsModals/OrderStatusConfirmationModal';
import { updateEReceiptPreferences } from '../apis/eReceipt';
import { ERROR_ALERT_TEXT, YES } from '../constants';
import { RetrieveErrorContext } from '../contexts/RetrieveErrorContext';

export const saveChangeButton = 'order-status-save-change';

export function Preferences({
  email, phoneNumber, managedAccount, accountOwner, isEReceiptEnabled, isOrderCommsEnabled
}) {
  const {
    openErrorAlert, openOrderOrEReceiptSuccessAlert, closeOrderStatusSuccessAlert,
  } = useContext(OrderBannerContext);

  const {
    addError
  } = React.useContext(RetrieveErrorContext);

  const [successBanners, setSuccessBanners] = React.useState([]);
  const [errorBanners, setErrorBanners] = React.useState([]);
  // OrderStatus
  const [tableData, setTableData] = React.useState(initialOrderPreferences());
  const [selected, setSelected] = React.useState('');
  const [initial, setInitial] = React.useState('');
  const [openPhoneModal, setOpenPhoneModal] = React.useState(false);
  const handleRetrieve = setupHandleOrderCommunicationsRetrieve(setSelected, setInitial, managedAccount);
  //  EReceipt
  const [initialEReceipt, setInitialEReceipt] = React.useState(
    { eReceiptPref: false, primaryFlag: 'N', marketingPref: false });
  const [eReceiptStatus, setEReceiptStatus] = React.useState('');
  const handleEReceiptPrefRetrieve = setupEReceiptPreferenceUpdate(setEReceiptStatus, setInitialEReceipt);

  const handleEReceiptInitialState = (value) => {
    setInitialEReceipt(value);
  };

  const handleEReceiptUpdatedState = (value) => {
    setEReceiptStatus(value);
  };

  function handleError(err) {
    setErrorBanners((prev) => [...prev, ERROR_ALERT_TEXT]);
  }

  function handleSuccess(res) {
    setSuccessBanners((prev) => [...prev, determineSuccessText(selected, phoneNumber, email)]);
    handleRetrieve(res);
  }

  function handleEReceiptSuccess(res) {
    setSuccessBanners((prev) => [...prev, determineEReceiptSuccessText(eReceiptStatus, email)]);
    handleEReceiptPrefRetrieve(res);
  }

  function handleRetrieveError() {
    addError();
  }

  useEffect(() => {
    retrieveOrderPreferences(handleRetrieve, handleRetrieveError);
  }, []);

  const handleOpen = () => {
    setOpenPhoneModal(true);
  };

  useEffect(() => {
    if (successBanners.length > 0) {
      openOrderOrEReceiptSuccessAlert(successBanners);
    }
    if (errorBanners.length >= 1) {
      openErrorAlert(errorBanners[0]);
    }
  }, [successBanners, errorBanners]);

  function handleOrderStatusUpdate() {
    if (selected.length && initial !== selected) {
      if (selected !== 'email') {
        handleOpen();
      } else {
        updateOrderPreferences(tableData, selected, (res) => {
          handleSuccess(res);
        },
        (err) => {
          handleError(err);
        });
      }
    }
  }

  function handleEReceiptUpdate() {
    if (typeof eReceiptStatus === 'boolean' && initialEReceipt.eReceiptPref !== eReceiptStatus) {
      updateEReceiptPreferences(email, eReceiptStatus, initialEReceipt.marketingPref,
        initialEReceipt.primaryFlag, (res) => {
          handleEReceiptSuccess(res);
        },
        (err) => {
          handleError(err, initialEReceipt.eReceiptPref);
        });
    }
  }

  const saveChanges = () => {
    closeOrderStatusSuccessAlert();
    setSuccessBanners([]);
    setErrorBanners([]);
    if (managedAccount === YES) {
      handleOrderStatusUpdate();
    }
    handleEReceiptUpdate();
    setEReceiptStatus('');
  };

  function determineStatement() {
    return managedAccount === YES ? (
      <Typography variant="body-base" height="none">
        Update your preferred method of communication when it comes to order status, updates, and
        more.
        Order status preferences only apply to orders placed with an account representative.
      </Typography>
    )
      : (
        <Typography variant="body-base" height="none">
          Update your preference to receive eReceipt emails.
        </Typography>
      );
  }

  return (
    <div className="sui-grid sui-gap-4">
      {determineStatement()}
      <div className="sui-grid sui-grid-cols-1 sm:sui-grid-cols-2 lg:sui-grid-cols-4 xl:sui-grid-cols-6 sui-gap-3">
        {isEReceiptEnabled && accountOwner === YES && (
          <div className="sm:sui-col-span-1 lg:sui-col-span-2 xl:sui-col-span-3">
            <ProEReceiptPreferences
              sendInitialEReceipt={handleEReceiptInitialState}
              sendUpdated={handleEReceiptUpdatedState}
            />
          </div>
        )}
        <div className="sm:sui-col-span-1 lg:sui-col-span-2 xl:sui-col-span-3">
          {(managedAccount === YES && isOrderCommsEnabled) && (
            <OrderPreferencesCard
              tableData={tableData}
              setSelected={setSelected}
              preferences={selected}
            />
          )}
        </div>
      </div>
      <div className="sui-grid sui-grid-cols-8">
        <div className="hidden sm:sui-col-span-4 md:sui-col-span-5" />
        <div className="sui-col-span-8 sm:sui-col-span-4 md:sui-col-span-3 sui-grid sui-gap-y-4">
          <Button aria-label={saveChangeButton} fullWidth onClick={saveChanges}>
            Save Changes
          </Button>
        </div>
      </div>
      {openPhoneModal
        && OrderStatusConfirmationModal(
          tableData,
          selected,
          phoneNumber,
          email,
          handleRetrieve,
          setOpenPhoneModal,
          setErrorBanners,
          setSuccessBanners
        )}
    </div>
  );
}

function OrderPreferences({
  email, phoneNumber, managedAccount, accountOwner, isEReceiptEnabled, isOrderCommsEnabled
}) {
  return (
    <ErrorBoundary>
      <BannerProvider>
        <Preferences
          email={email}
          phoneNumber={phoneNumber}
          managedAccount={managedAccount}
          accountOwner={accountOwner}
          isEReceiptEnabled={isEReceiptEnabled}
          isOrderCommsEnabled={isOrderCommsEnabled}
        />
      </BannerProvider>
    </ErrorBoundary>
  );
}

export default OrderPreferences;

const proptypes = {
  email: PropTypes.string.isRequired,
  phoneNumber: PropTypes.string.isRequired,
  managedAccount: PropTypes.string.isRequired,
  isEReceiptEnabled: PropTypes.bool.isRequired,
  isOrderCommsEnabled: PropTypes.bool.isRequired,
  accountOwner: PropTypes.string.isRequired,
};
OrderPreferences.propTypes = proptypes;

Preferences.propTypes = proptypes;
